import { supabase } from "./supabase"

/**
 * Verifies a TOTP code against `enrollFactorId` when enrolling, otherwise against every
 * verified TOTP factor. Users re-enrolled across sessions can hold several verified
 * factors; checking only the first rejected valid codes from the authenticator in use.
 */
export async function verifyTotpCode(code: string, enrollFactorId: string | null): Promise<void> {
  const factorIds = enrollFactorId ? [enrollFactorId] : await listVerifiedTotpFactorIds()
  if (factorIds.length === 0) throw new Error("No authenticator factor found — try signing out and back in.")

  let lastError: Error | null = null
  for (const factorId of factorIds) {
    const { data: challenge, error: chErr } = await supabase.auth.mfa.challenge({ factorId })
    if (chErr || !challenge) {
      lastError = chErr ?? new Error("MFA challenge failed")
      continue
    }
    const { error: vErr } = await supabase.auth.mfa.verify({ factorId, challengeId: challenge.id, code })
    if (!vErr) return
    lastError = vErr
  }
  throw lastError ?? new Error("Verification failed")
}

/** Enrollment in another tab or remount deletes this tab's unverified factor. */
export function isFactorNotFoundError(error: unknown): boolean {
  return typeof error === "object" && error !== null && "code" in error && error.code === "mfa_factor_not_found"
}

async function listVerifiedTotpFactorIds(): Promise<string[]> {
  const { data, error } = await supabase.auth.mfa.listFactors()
  if (error) throw error
  return (data?.totp ?? []).filter((f) => f.status === "verified").map((f) => f.id)
}
